/**
 * Live Quiz Settings Component
 *
 * Detailed settings panel for live quiz configuration.
 */

import type { LiveQuizSettingsConfig } from '../../types/form';

interface LiveQuizSettingsProps {
  settings: LiveQuizSettingsConfig;
  onChange: (settings: LiveQuizSettingsConfig) => void;
}

export function LiveQuizSettings({ settings, onChange }: LiveQuizSettingsProps) {
  const updateSettings = (updates: Partial<LiveQuizSettingsConfig>) => {
    onChange({ ...settings, ...updates });
  };

  return (
    <div className="mt-4 space-y-6 border-t border-purple-200 dark:border-purple-700 pt-4">
      {/* Timing Section */}
      <div className="space-y-3">
        <h5 className="text-xs font-semibold text-purple-800 dark:text-purple-200 uppercase tracking-wide flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Timing
        </h5>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
              Time per question
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={5}
                max={120}
                value={settings.questionTimeSeconds}
                onChange={(e) => updateSettings({ questionTimeSeconds: Number(e.target.value) })}
                className="w-20 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">seconds</span>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
              Auto-advance delay
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={30}
                value={settings.autoAdvanceDelaySeconds}
                onChange={(e) => updateSettings({ autoAdvanceDelaySeconds: Number(e.target.value) })}
                className="w-20 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                disabled={!settings.autoAdvance}
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">seconds</span>
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.autoAdvance}
            onChange={(e) => updateSettings({ autoAdvance: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Auto-advance to next question</span>
        </label>
      </div>

      {/* Scoring Section */}
      <div className="space-y-3">
        <h5 className="text-xs font-semibold text-purple-800 dark:text-purple-200 uppercase tracking-wide flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Scoring
        </h5>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
              Max points per question
            </label>
            <input
              type="number"
              min={100}
              max={10000}
              step={100}
              value={settings.maxPointsPerQuestion}
              onChange={(e) => updateSettings({ maxPointsPerQuestion: Number(e.target.value) })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
              Min points (slow answer)
            </label>
            <input
              type="number"
              min={0}
              max={settings.maxPointsPerQuestion}
              step={100}
              value={settings.minPointsPerQuestion}
              onChange={(e) => updateSettings({ minPointsPerQuestion: Number(e.target.value) })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              disabled={!settings.timeBasedScoring}
            />
          </div>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.timeBasedScoring}
            onChange={(e) => updateSettings({ timeBasedScoring: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Time-based scoring (faster = more points)</span>
        </label>

        <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-100 dark:border-purple-800">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.enableStreakBonus}
              onChange={(e) => updateSettings({ enableStreakBonus: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Enable streak bonus</span>
          </label>

          {settings.enableStreakBonus && (
            <div className="mt-3 ml-6 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Streak threshold
                </label>
                <input
                  type="number"
                  min={2}
                  max={10}
                  value={settings.streakThreshold}
                  onChange={(e) => updateSettings({ streakThreshold: Number(e.target.value) })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">consecutive correct</p>
              </div>

              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Bonus multiplier
                </label>
                <input
                  type="number"
                  min={1.1}
                  max={3}
                  step={0.1}
                  value={settings.streakBonusMultiplier}
                  onChange={(e) => updateSettings({ streakBonusMultiplier: Number(e.target.value) })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">e.g., 1.2 = +20%</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Display Section */}
      <div className="space-y-3">
        <h5 className="text-xs font-semibold text-purple-800 dark:text-purple-200 uppercase tracking-wide flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Display
        </h5>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.showQuestionOnPlayerDevice}
            onChange={(e) => updateSettings({ showQuestionOnPlayerDevice: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Show question text on player devices</span>
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
          If disabled, only answer options are shown (question on host screen only)
        </p>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.showCorrectAnswer}
            onChange={(e) => updateSettings({ showCorrectAnswer: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Reveal correct answer after each question</span>
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.showAnswerDistribution}
            onChange={(e) => updateSettings({ showAnswerDistribution: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Show answer distribution chart</span>
        </label>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.showLeaderboardAfterQuestion}
              onChange={(e) => updateSettings({ showLeaderboardAfterQuestion: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Show leaderboard</span>
          </label>

          {settings.showLeaderboardAfterQuestion && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Top</span>
              <input
                type="number"
                min={3}
                max={20}
                value={settings.leaderboardSize}
                onChange={(e) => updateSettings({ leaderboardSize: Number(e.target.value) })}
                className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">players</span>
            </div>
          )}
        </div>
      </div>

      {/* Gameplay Section */}
      <div className="space-y-3">
        <h5 className="text-xs font-semibold text-purple-800 dark:text-purple-200 uppercase tracking-wide flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Gameplay
        </h5>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.allowLateJoin}
            onChange={(e) => updateSettings({ allowLateJoin: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Allow players to join after game starts</span>
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.randomizeQuestions}
            onChange={(e) => updateSettings({ randomizeQuestions: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Randomize question order</span>
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.randomizeOptions}
            onChange={(e) => updateSettings({ randomizeOptions: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Randomize answer options</span>
        </label>
      </div>

      {/* Audio Section */}
      <div className="space-y-3">
        <h5 className="text-xs font-semibold text-purple-800 dark:text-purple-200 uppercase tracking-wide flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
          Audio
        </h5>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.enableSounds}
            onChange={(e) => updateSettings({ enableSounds: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Enable sound effects</span>
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
          Plays sounds for countdown, correct/wrong answers, and transitions
        </p>
      </div>

      {/* Tips */}
      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
        <h5 className="text-xs font-semibold text-purple-800 dark:text-purple-200 mb-2">Tips</h5>
        <ul className="text-xs text-purple-700 dark:text-purple-300 space-y-1">
          <li>- 20 seconds per question works well for most quizzes</li>
          <li>- Enable streak bonuses to encourage consistent performance</li>
          <li>- Showing the leaderboard keeps players engaged</li>
          <li>- Consider disabling late join for competitive quizzes</li>
        </ul>
      </div>
    </div>
  );
}

export default LiveQuizSettings;
